import { store } from '../admin-store.js';
import { renderNotificationBell, bindNotifications } from './notifications.js';

const routeTitles = {
    '/': 'Дашборд',
    '/accounts': 'Аккаунты',
    '/users': 'Пользователи',
    '/staff': 'Персонал',
    '/appointments': 'Записи',
    '/visits': 'Визиты',
    '/transactions': 'Транзакции',
    '/calls': 'Звонки',
    '/bots': 'Боты',
    '/unified-bot': 'Единый бот',
    '/reviews': 'Отзывы',
    '/billing': 'Биллинг',
    '/feature-labels': 'Фичи тарифов',
    '/stats': 'Статистика',
    '/traffic': 'Трафик',
    '/seo': 'SEO',
    '/partners': 'Партнёры',
    '/map': 'Карта',
    '/market': 'Рынок',
    '/mail': 'Почта',
    '/roles': 'Роли и права',
    '/settings': 'Настройки',
    '/system': 'Система',
    '/admins': 'Админы',
    '/audit': 'Аудит',
    '/login': 'Вход',
};

export function getPageTitle(path) {
    if (!path) return 'Дашборд';
    // Check exact match first
    if (routeTitles[path]) return routeTitles[path];
    // Check prefix match for detail pages
    if (path.startsWith('/accounts/')) return 'Аккаунт';
    if (path.startsWith('/users/')) return 'Пользователь';
    return 'BWClients Admin';
}

export function renderHeader() {
    const hash = window.location.hash.slice(1) || '/';
    const path = hash.split('?')[0];
    const title = getPageTitle(path);
    const userName = store.user?.name || store.user?.email || 'Admin';
    const userRole = store.user?.role || 'admin';
    const roleBadge = userRole === 'superadmin' ? 'Super Admin' : 'Admin';

    // Build breadcrumbs
    const parts = path.split('/').filter(Boolean);
    let breadcrumbs = '<a href="#/" class="breadcrumb-item">Главная</a>';
    if (parts.length > 0) {
        const parentPath = '#/' + parts[0];
        const parentTitle = routeTitles['/' + parts[0]] || parts[0];
        breadcrumbs += `<span class="breadcrumb-sep">/</span><a href="${parentPath}" class="breadcrumb-item">${parentTitle}</a>`;
    }
    if (parts.length > 1) {
        // For account detail pages, show ID as placeholder (page will update via JS)
        const detailLabel = path.startsWith('/accounts/') ? `#${parts[1]}` : parts[1];
        breadcrumbs += `<span class="breadcrumb-sep">/</span><span class="breadcrumb-item current">${detailLabel}</span>`;
    }

    return `
        <header class="admin-header">
            <div class="header-left">
                <button class="hamburger" id="hamburgerBtn" aria-label="Menu">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>
                </button>
                <div class="header-title-section">
                    <h1 class="header-title">${title}</h1>
                    <nav class="breadcrumbs">${breadcrumbs}</nav>
                </div>
            </div>
            <div class="header-right">
                <button class="btn btn--ghost btn--sm header-search-btn" id="headerCmdK" title="Поиск (Cmd+K)" aria-label="Поиск">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/></svg>
                    <kbd style="font-size:10px;padding:1px 4px;border:1px solid var(--border-color);border-radius:3px;margin-left:4px;color:var(--text-muted)">&#8984;K</kbd>
                </button>
                <button class="btn btn--ghost btn--sm" id="themeToggle" title="Переключить тему" aria-label="Тема">
                    ${document.documentElement.dataset.theme === 'light'
                        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"/></svg>'
                        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"/></svg>'
                    }
                </button>
                ${renderNotificationBell()}
                <div class="header-user">
                    <span class="header-user-name">${userName}</span>
                    <span class="badge badge--${userRole === 'superadmin' ? 'accent' : 'default'}">${roleBadge}</span>
                </div>
                <button class="btn btn--ghost btn--sm" id="headerLogout" title="Выйти" aria-label="Выйти">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"/></svg>
                </button>
            </div>
        </header>`;
}

export function bindHeaderExtras() {
    bindNotifications();

    // Cmd+K search button
    document.getElementById('headerCmdK')?.addEventListener('click', () => {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
    });

    // Theme toggle
    document.getElementById('themeToggle')?.addEventListener('click', () => {
        const html = document.documentElement;
        const isLight = html.dataset.theme === 'light';
        html.dataset.theme = isLight ? '' : 'light';
        localStorage.setItem('bwc_admin_theme', isLight ? 'dark' : 'light');
        // Swap icon without full re-render
        const btn = document.getElementById('themeToggle');
        if (btn) {
            btn.innerHTML = isLight
                ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"/></svg>'
                : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"/></svg>';
        }
    });
}

// Init theme from localStorage on load
export function initTheme() {
    const saved = localStorage.getItem('bwc_admin_theme');
    if (saved === 'light') {
        document.documentElement.dataset.theme = 'light';
    }
}
